import { collection, addDoc, getDocs, query, where, orderBy, runTransaction, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase/config';
import { CheckIn, CheckInComment } from '../types';

const checkInsCollection = collection(db, 'checkins');

export const checkInToEvent = async (
  eventId: string,
  userId: string,
  userName: string,
  userPhoto?: string | null,
  message?: string
): Promise<string> => {
  try {
    return await runTransaction(db, async (transaction) => {
      // Check if user already checked in
      const q = query(
        checkInsCollection,
        where('eventId', '==', eventId),
        where('userId', '==', userId)
      );
      const checkInDocs = await getDocs(q);
      
      if (!checkInDocs.empty) {
        throw new Error('User already checked in to this event');
      }

      const checkInData = {
        eventId,
        userId,
        userName,
        userPhoto: userPhoto || null,
        message: message || null,
        createdAt: new Date(),
        likes: [],
        comments: []
      };

      const newCheckInRef = await addDoc(checkInsCollection, checkInData);

      // Update event check-ins count
      const eventRef = doc(db, 'events', eventId);
      const eventDoc = await transaction.get(eventRef);
      
      if (eventDoc.exists()) {
        const currentCheckins = eventDoc.data().checkins || 0;
        transaction.update(eventRef, {
          checkins: currentCheckins + 1
        });
      }

      return newCheckInRef.id;
    });
  } catch (error) {
    console.error('Error checking in:', error);
    throw error;
  }
};

export const likeCheckIn = async (checkInId: string, userId: string): Promise<void> => {
  try {
    const checkInRef = doc(db, 'checkins', checkInId);
    await updateDoc(checkInRef, {
      likes: arrayUnion(userId)
    });
  } catch (error) {
    console.error('Error liking check-in:', error);
    throw error;
  }
};

export const unlikeCheckIn = async (checkInId: string, userId: string): Promise<void> => {
  try {
    const checkInRef = doc(db, 'checkins', checkInId);
    await updateDoc(checkInRef, {
      likes: arrayRemove(userId)
    });
  } catch (error) {
    console.error('Error unliking check-in:', error);
    throw error;
  }
};

export const addComment = async (
  checkInId: string,
  userId: string,
  userName: string,
  content: string,
  userPhoto?: string
): Promise<void> => {
  try {
    const checkInRef = doc(db, 'checkins', checkInId);
    const comment: CheckInComment = {
      id: Date.now().toString(),
      userId,
      userName,
      content,
      userPhoto,
      createdAt: new Date()
    };
    
    await updateDoc(checkInRef, {
      comments: arrayUnion(comment)
    });
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

export const getEventCheckIns = async (eventId: string): Promise<CheckIn[]> => {
  try {
    const q = query(
      checkInsCollection,
      where('eventId', '==', eventId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        comments: data.comments?.map((comment: any) => ({
          ...comment,
          createdAt: comment.createdAt?.toDate() || new Date()
        })) || []
      } as CheckIn;
    });
  } catch (error) {
    console.error('Error getting check-ins:', error);
    return [];
  }
};

export const getCheckInByUserAndEvent = async (
  eventId: string,
  userId: string
): Promise<CheckIn | null> => {
  try {
    const q = query(
      checkInsCollection,
      where('eventId', '==', eventId),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      comments: data.comments?.map((comment: any) => ({
        ...comment,
        createdAt: comment.createdAt?.toDate() || new Date()
      })) || []
    } as CheckIn;
  } catch (error) {
    console.error('Error getting user check-in:', error);
    return null;
  }
};